import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { Textarea } from "@/components/podkit/forms/Textarea";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useUpdatePrompt, type PlainPrompt } from "@/queries/prompt-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useState, type FC } from "react";

type Props = {
    command: PlainPrompt;
    onClose: () => void;
};

export const EditCommandModal: FC<Props> = ({ command, onClose }) => {
    const [name, setName] = useState(command.metadata?.name || "");
    const [commandText, setCommandText] = useState(command.spec?.command || "");
    const [description, setDescription] = useState(command.metadata?.description || "");
    const [prompt, setPrompt] = useState(command.spec?.prompt || "");

    const updatePrompt = useUpdatePrompt();
    const { toast } = useToast();

    const handleSave = useCallback(async () => {
        try {
            await updatePrompt.mutateAsync({
                promptId: command.id,
                metadata: {
                    name: name.trim(),
                    description: description.trim(),
                },
                spec: {
                    prompt: prompt.trim(),
                    command: commandText.trim(),
                },
            });
            toast({
                title: "Command updated successfully",
                description: `/${commandText} has been updated`,
            });
            onClose();
        } catch (error) {
            console.error(error);
            toast({
                title: "Failed to update command",
                description: formatError(error),
            });
        }
    }, [command.id, name, commandText, description, prompt, updatePrompt, toast, onClose]);

    const isValid = name.trim() && commandText.trim() && description.trim() && prompt.trim();

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent
                className="max-w-[600px]"
                data-testid="edit-command-modal"
                data-track-location={TrackLocations.EditCommandModal}
            >
                <DialogHeader>
                    <DialogTitle>Edit Agent Command</DialogTitle>
                    <DialogDescription>Update the command details</DialogDescription>
                </DialogHeader>

                <DialogBody className="space-y-4">
                    <InputField id="edit-command-name" label="Name" hint="The name of the command">
                        <Input
                            id="edit-command-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Command name"
                            data-testid="edit-command-name-input"
                        />
                    </InputField>

                    <InputField
                        id="edit-command-trigger"
                        label="Command"
                        hint="The command that will be triggered when the agent receives this command"
                    >
                        <Input
                            id="edit-command-trigger"
                            value={commandText}
                            onChange={(e) => setCommandText(e.target.value)}
                            placeholder="trigger-word"
                            data-testid="edit-command-trigger-input"
                            prefix="/"
                        />
                    </InputField>

                    <InputField
                        id="edit-command-description"
                        label="Description"
                        hint="Brief description of what this command does"
                    >
                        <Input
                            id="edit-command-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Brief description of what this command does"
                            data-testid="edit-command-description-input"
                        />
                    </InputField>

                    <InputField
                        id="edit-command-prompt"
                        label="Prompt"
                        hint={`The prompt that will be sent to the AI agent (${prompt.length.toLocaleString()} / ${(20000).toLocaleString()} characters)`}
                    >
                        <Textarea
                            id="edit-command-prompt"
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder="The prompt that will be sent to the AI agent"
                            className="resize-none"
                            minHeight={120}
                            maxHeight={300}
                            data-testid="edit-command-prompt-textarea"
                            maxLength={20000}
                        />
                    </InputField>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button
                        onClick={handleSave}
                        disabled={!isValid}
                        loading={updatePrompt.isPending}
                        data-testid="update-command-button"
                        data-tracking-id="update-agent-command-edit-command-modal"
                    >
                        Update Command
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
